import { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ChatMessage, postQuery, getChatHistory, getSessionStatus } from '../IdeationService';
//import { fieldAPIKeyMap } from '../../NewIdeationXPLR/NewIdeation/constants';
import { ENVIRONMENT_TYPE } from '../../../config/env';
import ShowToast from '../../../common/components/ShowToast';
import { ToastTypeEnum } from '../../../constants/solutionXPLR';
import { RootState } from '../../../store';
import { updateIdeationFieldState } from '../../NewIdeationXPLR/state';
import { getFieldTypeFromCategory, getConversationTypeFromField } from '../utils';
import { DRAFT_STORAGE_KEY } from '../constants';
import { fieldAPIKeyMap } from '../../NewIdeationXPLR/shared/constants';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';

interface UseChatMessagingProps {
  chatId: string;
  setChatId: (id: string) => void;
  /** Component field key (e.g. currentProcess) for ideation mapping */
  currentField: string;
  /** Canonical backend field_type (e.g. current_process or ..._solutionxplr) */
  currentFieldType: string;
  inputMessage: string;
  setInputMessage: (msg: string) => void;
  organization_id: string;
  navigate: (path: string) => void;
  useCaseDetails: Record<string, any>;
  setUseCaseDetails: (details: any) => void;
  contextState: any;
  setContextState: (state: any) => void;
  allContextData: any[];
  setAllContextData: (data: any[]) => void;
  versionData: Record<string, any>;
  loadSessions: (fieldType?: string) => void;
  selectedCategory: string;
  selectedSessionName: string;
  messages: ChatMessage[];
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  /** Called with an optimistic session object immediately after a new session is created */
  onNewSession?: (session: any) => void;
}

export const useChatMessaging = ({
  chatId,
  setChatId,
  currentField,
  currentFieldType,
  inputMessage,
  setInputMessage,
  organization_id,
  navigate,
  useCaseDetails,
  setUseCaseDetails,
  contextState,
  setContextState,
  allContextData,
  setAllContextData,
  versionData,
  loadSessions,
  selectedCategory,
  selectedSessionName,
  messages,
  setMessages,
  onNewSession,
}: UseChatMessagingProps) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const [isSending, setIsSending] = useState<Record<string, boolean>>({});
  const [reportLoading, setReportLoading] = useState<boolean>(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [failedMessageData, setFailedMessageData] = useState<any>(null);
  const [diagramXml, setDiagramXml] = useState<string>('');
  const [isDiagramLoading, setIsDiagramLoading] = useState<boolean>(false);
  const [diagramStatus, setDiagramStatus] = useState<string>('');
  const [conversationType, setConversationType] = useState<string>('');

  const user = useSelector((state: RootState) => state.user);

  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const processingMessageIdsRef = useRef<Record<string, string>>({});
  // Prevent duplicate auto-submission when history is empty but status has completed.
  const autoSubmittedFirstMessageRef = useRef<Record<string, boolean>>({});

  const { detailsText, selectedContexts, taxonomy, uploadedFiles } = useSelector(
    (state: RootState) => state.newIdeation
  );

  const handleSendMessage = useCallback(
    async (query?: string, isFirstMessage: boolean = false, id: string = '') => {
      let message = inputMessage || query || '';
      if (isFirstMessage) message = detailsText;
      const noMessage = !message.trim();

      const valid = isFirstMessage
        ? uploadedFiles?.length || selectedContexts?.length
        : contextState.uploadedFiles?.length || contextState.selectedContexts?.length;

      const useCaseId = id && isFirstMessage ? id : chatId;

      if ((noMessage && !valid) || isSending[useCaseId] || uploadingImage) return;

      const currentMessage = message;
      setInputMessage('');
      setIsSending((prev) => ({ ...prev, [useCaseId]: true }));

      const selectedContextsArr = contextState.selectedContexts.reduce(
        (acc: any[], contextId: string) => {
          const contextProcess = contextState.contextProcesses.find((p: any) => p.id === contextId);
          if (contextProcess?.context_knowledge) {
            acc.push(contextProcess.context_knowledge);
          }
          return acc;
        },
        [] as any[]
      );

      const tempUserMessage: ChatMessage = {
        id: `temp-${Date.now()}`,
        query: {
          report: '',
          impact: '',
          user_query: currentMessage,
        },
        answer: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        status: 'pending',
        metadata: {},
        timeTaken: 0,
        fileSummary: '',
        files: { data: isFirstMessage ? uploadedFiles : contextState.uploadedFiles },
        context_knowledge: {
          data: isFirstMessage ? selectedContexts : selectedContextsArr || [],
        },
      };

      setMessages((prev) => [...prev, tempUserMessage]);

      try {
        setContextState((prev: any) => ({
          ...prev,
          uploadedFiles: prev.uploadedFiles.map((file: any) => ({
            ...file,
            isAnalyzing: file.filePath && !file.fileData ? true : false,
          })),
        }));

        const filesSelected = isFirstMessage ? uploadedFiles : contextState.uploadedFiles;

        const filesToSend = (filesSelected || [])?.map((file: any) => ({
          id: file.id,
          fileName: file?.fileName || file?.name,
          fileUrl: file.fileUrl || file?.url || '',
          fileData: file.fileData || '',
          fileSize: file.fileSize || file?.size || '',
          env: ENVIRONMENT_TYPE,
        }));

        const contextsToSend = isFirstMessage
          ? selectedContexts || []
          : [...allContextData, ...selectedContextsArr];

        const report =
          versionData && Object.keys(versionData).length > 0
            ? versionData[Object.keys(versionData)[Object.keys(versionData).length - 1]]?.summary
            : '';

        // Capture the backend field_type NOW (before any async work) so that even if
        // category changes during the request, we keep the original field_type.
        const submittedFieldType = currentFieldType || fieldAPIKeyMap[currentField] || currentField;

        let userQuery: any = {
          report: report,
          user_query: currentMessage,
          context: contextsToSend,
          taxonomy: taxonomy,
          user_file_data: filesToSend,
          field: submittedFieldType,
          organization_id: organization_id,
          env_type: ENVIRONMENT_TYPE,
        };

        if (selectedContextsArr?.length) {
          // const contextsNames = selectedContextsArr
          //   ?.map((ctx: any) => ctx?.use_case_title || 'Unknown')
          //   .join(', ');
          userQuery.user_query = `${userQuery.user_query ? `${userQuery.user_query}` : ''}`;
        }

        if (filesToSend?.length) {
         // const fileNames = filesToSend?.map((file: any) => file?.fileName).join(', ');
          userQuery.user_query = `${userQuery.user_query ? `${userQuery.user_query}` : ''}`;
        }

        if (isFirstMessage && id && !chatId) {
          setChatId(id);
          // Clear landing page draft and Redux state after first successful submission
          dispatch(updateIdeationFieldState({ field: 'detailsText', value: '' }));
          dispatch(updateIdeationFieldState({ field: 'uploadedFiles', value: [] }));
          dispatch(updateIdeationFieldState({ field: 'selectedContexts', value: [] }));

          // Clear the shared draft key (single key per-org, no fieldType suffix)
          const sharedDraftKey = organization_id
            ? `${DRAFT_STORAGE_KEY}_${organization_id}`
            : DRAFT_STORAGE_KEY;
          localStorage.removeItem(sharedDraftKey);
        }

        if (isFirstMessage && useCaseId)
          navigate(
            `/data-xplr/${useCaseId}?selectedCategory=${encodeURIComponent(submittedFieldType)}`
          );

        const payload = {
          useCaseId: useCaseId,
          query: userQuery,
          domain: useCaseDetails?.domain || '',
          function: useCaseDetails?.function || '',
          department: useCaseDetails?.process_group || '',
          subDepartment: useCaseDetails?.process || '',
          files: isFirstMessage ? uploadedFiles : filesToSend || [],
          context: isFirstMessage ? selectedContexts : selectedContextsArr || [],
          tenantId: '6764029052789d00255f17a1',
          field_type: submittedFieldType,
          organization_id: organization_id,
          conversation_type: getConversationTypeFromField(submittedFieldType),
        };

        payload.query['user_id'] = user?.id || '';
        payload.query['user_name'] = `${user?.first_name} ${user?.last_name}`  || '';

        setContextState((prev: any) => ({
          ...prev,
          uploadedFiles: [],
          selectedContexts: [],
        }));

        const data = await postQuery(payload);
        // Use the captured field type (not selectedCategory which may have changed)
        loadSessions(submittedFieldType);


        // Immediately surface the new session in the sidebar (optimistic update)
        // so it's visible right away without waiting for the server refresh.
        if (isFirstMessage && onNewSession) {
          const sessionTitle = data?.sessionName || data?.session_name || 'Generating...';
          onNewSession({
            id: data.queryId || useCaseId,
            conversation_id: useCaseId,
            use_case_title: sessionTitle,
            session_name: sessionTitle === 'Generating...' ? '' : sessionTitle,
            created_by: 'me',
            updated_by: '',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            usedInProcess: submittedFieldType,
            field_type: submittedFieldType,
            ideation_intake: [],
            highlight: null,
          });
        }

        if (data?.sessionName) {
          setUseCaseDetails({
            ...useCaseDetails,
            title: data?.sessionName || '',
          });
        }

        setMessages((prev) => {
          const newMessages = [...prev];
          const lastMessage = newMessages[newMessages.length - 1];

          if (lastMessage && lastMessage.id.startsWith('temp-')) {
            newMessages[newMessages.length - 1] = {
              id: data.queryId || lastMessage.id,
              query: {
                report: '',
                impact: '',
                user_query: currentMessage,
              },
              answer: data.botMessage || '',
              discoveryContent: {
                report: data?.discoveryContent?.report || '',
                summary: data?.discoveryContent?.file_summary || '',
              },
              impact: data?.impact || '',
              version: data.version || '',
              created_at: lastMessage.created_at,
              updated_at: new Date().toISOString(),
              status: 'completed',
              metadata: {
                useCaseId: payload.useCaseId,
                discoveryContent: data.discoveryContent,
                sessionName: data.sessionName,
                report: data.report,
              },
              files: { data: isFirstMessage ? uploadedFiles : filesToSend || [] },
              timeTaken: 0,
              fileSummary: data?.discoveryContent?.file_summary || '',
              context_knowledge: {
                data: isFirstMessage ? selectedContexts : selectedContextsArr || [],
              },
            };
          }

          return newMessages;
        });

        setIsSending((prev) => ({ ...prev, [useCaseId]: true }));
        setChatId(useCaseId);
        setTimeout(() => {
          getPreviousChat(payload.useCaseId);
        }, 10000);
      } catch (error: any) {
        console.error('Error sending message:', error);
        const isLLMError =
          error?.message?.includes('Invalid analysis') || error?.message?.includes('LLM');

        ShowToast({
          message: isLLMError
            ? 'We could not correctly understand your uploaded file. Please try again.'
            : 'Failed to send message. Please try again.',
          type: ToastTypeEnum.Error,
        });

        setFailedMessageData({
          message: currentMessage,
          files: isFirstMessage ? uploadedFiles : contextState.uploadedFiles,
          contexts: isFirstMessage ? selectedContexts : contextState.selectedContexts,
        });

        setInputMessage(currentMessage);
        setContextState((prev: any) => ({
          ...prev,
          uploadedFiles: isFirstMessage ? uploadedFiles : contextState.uploadedFiles,
          selectedContexts: isFirstMessage ? selectedContexts : contextState.selectedContexts,
        }));

        setMessages((prev) => prev.filter((msg) => !msg.id.startsWith('temp-')));
        setIsSending((prev) => ({ ...prev, [useCaseId]: false }));
      }
    },
    [
      inputMessage,
      detailsText,
      uploadedFiles,
      selectedContexts,
      contextState,
      chatId,
      uploadingImage,
      isSending,
      allContextData,
      versionData,
      taxonomy,
      currentField,
      currentFieldType,
      organization_id,
      navigate,
      useCaseDetails,

      setChatId,
      setContextState,
      setUseCaseDetails,
      loadSessions,
      setInputMessage,
      onNewSession,
    ]
  );

  const getPreviousChat = useCallback(
    async (id: string) => {
      if (!id) return;

      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }

      setReportLoading(true);
      try {
        const activeFieldType = currentFieldType || fieldAPIKeyMap[currentField] || currentField;
        const conversationType = getConversationTypeFromField(activeFieldType);

        const [chatData, statusResponse] = await Promise.all([
          getChatHistory(id, activeFieldType, conversationType),
          getSessionStatus(organization_id, id, conversationType),
        ]);

        if (chatData?.total && chatData.total > 0) {
          const lastItem = chatData.data?.[chatData.data.length - 1];
          const context: any[] = lastItem?.query?.context ?? [];
          setAllContextData(context);

          if (chatData?.parent_metadata?.session_name) {
            const newSessionName = chatData.parent_metadata.session_name;
            if (newSessionName !== useCaseDetails?.title) {
              setUseCaseDetails((prev: any) => ({
                ...prev,
                title: newSessionName || '',
              }));

              if (
                onNewSession &&
                newSessionName &&
                newSessionName !== 'Untitled Session' &&
                newSessionName !== 'Untitled Process' &&
                newSessionName !== 'Generating...'
              ) {
                onNewSession({
                  id: id,
                  conversation_id: id,
                  use_case_title: newSessionName,
                  session_name: newSessionName,
                });
              }
            }
          }

          if (chatData?.parent_metadata?.diagram_content)
            setDiagramXml(chatData?.parent_metadata?.diagram_content);

          let chatStatus = statusResponse?.data?.status || 'completed';

          if (statusResponse?.data?.conversationType) {
            setConversationType(statusResponse.data.conversationType);
          }

          if (statusResponse?.data?.is_diagram_status) {
            setDiagramStatus(statusResponse.data.is_diagram_status);
          }

          if (chatStatus === 'failed') {
            setIsSending((prev) => ({ ...prev, [id]: false }));

            const processingMsgId = processingMessageIdsRef.current[id];
            if (processingMsgId) {
              delete processingMessageIdsRef.current[id];
            }
            setMessages(chatData.data.filter((msg: ChatMessage) => msg.id !== processingMsgId));

            const lastMessage = statusResponse?.data?.lastMessage;
            if (lastMessage?.user_query && chatId === id) {
              setInputMessage(lastMessage.user_query);
              setContextState((prev: any) => ({
                ...prev,
                uploadedFiles: lastMessage.user_file_data || [],
                selectedContexts: lastMessage.context || [],
              }));
              ShowToast({
                message: 'The previous message failed to process. Please try again.',
                type: ToastTypeEnum.Error,
              });
            }
          } else if (chatStatus === 'processing') {
            setIsSending((prev) => ({ ...prev, [id]: true }));

            const lastMessage = statusResponse?.data?.lastMessage;
            const stableProcessingMsgId = `processing-${id}`;

            if (lastMessage?.user_query && chatId === id) {
              const alreadyExists = processingMessageIdsRef.current[id];

              if (!alreadyExists) {
                processingMessageIdsRef.current[id] = stableProcessingMsgId;
              }

              const tempProcessingMessage: ChatMessage = {
                id: stableProcessingMsgId,
                query: { ...lastMessage },
                answer: '',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                status: 'pending',
                metadata: {},
                timeTaken: 0,
                fileSummary: '',
                files: { data: lastMessage.user_file_data || [] },
                context_knowledge: {
                  data: lastMessage.context || [],
                },
              };

              setMessages([...chatData.data, tempProcessingMessage]);
            }

            pollingTimeoutRef.current = setTimeout(() => {
              getPreviousChat(id);
            }, 30000);
          } else {
            setIsSending((prev) => ({ ...prev, [id]: false }));

            const processingMsgId = processingMessageIdsRef.current[id];
            if (processingMsgId) {
              delete processingMessageIdsRef.current[id];
            }
            setMessages(chatData.data.filter((msg: ChatMessage) => msg.id !== processingMsgId));
          }
        } else if (chatData?.total === 0) {
          let chatStatus = statusResponse?.data?.status || 'completed';
          const lastMessage = statusResponse?.data?.lastMessage;
          const sessionName = statusResponse?.data?.sessionName;

          if (
            Object.keys(lastMessage || {})?.length === 0 ||
            (Object.keys(lastMessage || {})?.length > 0 && selectedSessionName !== sessionName)
          ) {
            loadSessions(getFieldTypeFromCategory(selectedCategory));
          }

          if (chatStatus === 'failed') {
            setIsSending((prev) => ({ ...prev, [id]: false }));
            if (lastMessage?.user_query && chatId === id) {
              setInputMessage(lastMessage.user_query);
              setContextState((prev: any) => ({
                ...prev,
                uploadedFiles: lastMessage.user_file_data || [],
                selectedContexts: lastMessage.context || [],
              }));
              ShowToast({
                message: 'The previous message failed to process. Please try again.',
                type: ToastTypeEnum.Error,
              });
            }
          } else if (chatStatus === 'processing') {
            setIsSending((prev) => ({ ...prev, [id]: true }));

            const stableProcessingMsgId = `processing-${id}`;

            if (!processingMessageIdsRef.current[id] && lastMessage?.user_query && chatId === id) {
              const tempProcessingMessage: ChatMessage = {
                id: stableProcessingMsgId,
                query: { ...lastMessage },
                answer: '',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                status: 'pending',
                metadata: {},
                timeTaken: 0,
                fileSummary: '',
                files: { data: lastMessage.user_file_data || [] },
                context_knowledge: {
                  data: lastMessage.context || [],
                },
              };

              processingMessageIdsRef.current[id] = stableProcessingMsgId;
              setMessages([tempProcessingMessage]);
            }

            pollingTimeoutRef.current = setTimeout(() => {
              getPreviousChat(id);
            }, 5000);
          } else {
            setIsSending((prev) => ({ ...prev, [id]: false }));
            setAllContextData(selectedContexts);
            setInputMessage(useCaseDetails?.use_case || '');
            if (!autoSubmittedFirstMessageRef.current[id] && location.state?.isNewChat) {
              autoSubmittedFirstMessageRef.current[id] = true;
              await handleSendMessage(useCaseDetails?.use_case || detailsText, true, id);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching previous chat:', error);
        setIsSending((prev) => ({ ...prev, [id]: false }));
      } finally {
        setReportLoading(false);
      }
    },
    [
      chatId,
      currentField,
      currentFieldType,
      organization_id,
      selectedSessionName,
      selectedCategory,
      loadSessions,
      detailsText,
      handleSendMessage,
      setMessages,
      setAllContextData,
      setUseCaseDetails,
      setIsSending,
      setInputMessage,
      setContextState,
      setReportLoading,
    ]
  );

  // Handle cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
      processingMessageIdsRef.current = {};
    };
  }, []);

  return {
    messages,
    setMessages,
    isSending,
    setIsSending,
    reportLoading,
    setReportLoading,
    uploadingImage,
    setUploadingImage,
    failedMessageData,
    setFailedMessageData,
    handleSendMessage,
    getPreviousChat,
    pollingTimeoutRef,
    diagramXml,
    setDiagramXml,
    isDiagramLoading,
    setIsDiagramLoading,
    diagramStatus,
    setDiagramStatus,
    conversationType,
  };
};
